import { useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetBreadcrumbs } from '@/hooks/appbar';
import { useNavigation } from '@/hooks/navigation';
import { useUserAccess, useUserId } from '@/hooks/auth';
import { useMyPeopleAccess } from '@/store/access/access';
import { People } from '@/components/people/people';
import { People2 } from '@/components/people/people2';
import { Structure } from '@/components/people/structure';
import { Access as PeopleAccess } from '@/components/people/access';
import { Ministries } from '@/components/org/ministry';
import { Locations } from '@/components/org/campus';
import Users from '@/components/access/users';
import AccessGroups from '@/components/access/groups';
import AccessTeams from '@/components/access/teams';
import { Communications } from '@/components/communications/Communications';
import DashboardContent from '@/components/dashboard/DashboardContent';
import { ProfileMigration } from '@/components/migration/ProfileMigration';
import Platform from '@/components/platform';

// Dashboard content is shared as a separate component for reuse

export default function AllPage() {
  const { activeTab, setActiveTab, setVisibleTabs } = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const access = useUserAccess();
  const userId = useUserId();
  const peopleAccess = useMyPeopleAccess({});
  useSetBreadcrumbs([]);

  // Note: avoid early return before hooks to satisfy rules-of-hooks

  const isAdmin = Boolean(access?.admin);
  const canSeePeople = Boolean(access?.people || isAdmin);
  const canSeeGroups = canSeePeople;
  const canSeeTeams = isAdmin;
  const canSeeStructure = Boolean(
    isAdmin || peopleAccess.access.structure_view === 'allow',
  );
  const canSeeOrg = isAdmin;
  const canSeeUsers = isAdmin;
  const canSeeCommunications = Boolean(access?.communications || isAdmin);

  const tabs = useMemo(
    () => [
      {
        key: 'dashboard',
        title: 'Dashboard',
        visible: true,
        content: <DashboardContent />,
      },
      {
        key: 'people',
        title: 'People',
        state: 'deprecated',
        visible: canSeePeople,
        content: <People />,
      },
      {
        key: 'people2',
        title: 'People',
        visible: canSeePeople,
        content: <People2 />,
      },
      {
        key: 'groups',
        title: 'Groups',
        visible: canSeeGroups,
        content: <AccessGroups />,
      },
      {
        key: 'teams',
        title: 'Teams',
        visible: canSeeTeams,
        content: <AccessTeams />,
      },
      {
        key: 'communications',
        title: 'Communications',
        state: 'experimental',
        visible: canSeeCommunications,
        content: <Communications />,
      },
      {
        key: 'migration',
        title: 'Migration',
        state: 'experimental',
        visible: isAdmin,
        content: <ProfileMigration />,
      },
      {
        key: 'structure',
        title: 'Structure',
        visible: canSeeStructure,
        content: <Structure />,
      },
      {
        key: 'access',
        title: 'Access',
        visible: isAdmin,
        content: <PeopleAccess />,
      },
      {
        key: 'ministries',
        title: 'Ministries',
        visible: canSeeOrg,
        content: <Ministries />,
      },
      {
        key: 'locations',
        title: 'Locations',
        visible: canSeeOrg,
        content: <Locations />,
      },
      {
        key: 'users',
        title: 'Users',
        visible: canSeeUsers,
        content: <Users />,
      },
      {
        key: 'platform',
        title: 'Platform',
        visible: isAdmin,
        content: <Platform />,
      },
    ],
    [
      canSeePeople,
      canSeeGroups,
      canSeeTeams,
      canSeeStructure,
      canSeeOrg,
      canSeeUsers,
      canSeeCommunications,
      isAdmin,
    ],
  );

  const visibleTabs = useMemo(() => tabs.filter((t) => t.visible), [tabs]);

  // Ensure activeTab is valid, otherwise default to first visible tab
  const activeKey = visibleTabs.some((t) => t.key === activeTab)
    ? activeTab
    : visibleTabs[0]?.key || 'dashboard';

  // Sync activeTab with context when it becomes invalid
  useEffect(() => {
    if (activeKey !== activeTab) {
      setActiveTab(activeKey);
    }
  }, [activeKey, activeTab, setActiveTab]);

  // Sync visibleTabs with context when permissions change
  useEffect(() => {
    const simplifiedTabs = visibleTabs.map((t) => ({
      key: t.key,
      title: t.title,
      ...(t.state && { state: t.state as 'deprecated' | 'experimental' }),
    }));
    setVisibleTabs(simplifiedTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canSeePeople,
    canSeeStructure,
    canSeeOrg,
    canSeeUsers,
    isAdmin,
    setVisibleTabs,
  ]);

  // Map tab keys to routes
  const tabKeyToPath: Record<string, string> = useMemo(
    () => ({
      dashboard: '/',
      people: '/people-old',
      people2: '/people',
      groups: '/groups',
      teams: '/teams',
      structure: '/structure',
      access: '/access',
      ministries: '/ministries',
      locations: '/locations',
      users: '/users',
      communications: '/communications',
      migration: '/migration',
      platform: '/platform',
    }),
    [],
  );

  // Derive desired tab from current URL
  useEffect(() => {
    const rawPath = location.pathname.replace(/\/+$/, '');
    const parts = rawPath.split('/').filter(Boolean);
    // Support /, /all, /all/<tab>, and /<tab>; map path segments to tab keys
    const first = parts[0];
    const second = parts[1];
    const pathToTabKey: Record<string, string> = {
      people: 'people2',
      'people-old': 'people',
    };
    let desiredKey = 'dashboard';
    if (first && first !== 'all') {
      desiredKey = pathToTabKey[first] ?? first;
    } else if (first === 'all' && second) {
      desiredKey = pathToTabKey[second] ?? second;
    }

    const visibleKeys = new Set(visibleTabs.map((t) => t.key));
    if (!visibleKeys.has(desiredKey)) {
      // If URL tab isn't visible, fall back to first visible tab and fix the URL
      const fallback = visibleTabs[0]?.key || 'dashboard';
      if (activeTab !== fallback) {
        setActiveTab(fallback);
      }

      const targetPath = tabKeyToPath[fallback] || '/';

      if (location.pathname !== targetPath) {
        navigate(targetPath, { replace: true });
      }

      return;
    }

    // If URL-derived tab differs from state, sync state
    if (activeTab !== desiredKey) {
      setActiveTab(desiredKey);
    }
  }, [
    location.pathname,
    visibleTabs,
    activeTab,
    setActiveTab,
    tabKeyToPath,
    navigate,
  ]);

  // Avoid rendering until user is identified (consistent with other pages)
  if (!userId) {
    return <></>;
  }

  const activeTabData = visibleTabs.find((t) => t.key === activeKey);

  return <div className='w-full'>{activeTabData?.content}</div>;
}
